import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { UserAvatar } from "./UserAvatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { LayoutDashboard, LogOut, Settings, Users } from "lucide-react";

const dashboardPath = (role) =>
  role === "admin" ? "/admin" : role === "salesperson" ? "/sales" : "/dashboard";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-20 lg:h-24 flex items-center justify-between gap-2">
        <Link to="/" data-testid="nav-logo" className="flex items-center group shrink-0">
          <img src="/brandbloom-icon.png?v=2" alt="Brand Bloom" className="h-14 w-auto object-contain sm:hidden" />
          <img src="/brandbloom-logo.png" alt="Brand Bloom" className="hidden sm:block h-14 lg:h-20 w-auto object-contain" />
        </Link>

        <nav className="flex items-center gap-2 sm:gap-4">
          <Link
            to="/build"
            data-testid="nav-build-link"
            className="hidden sm:inline-block font-mono text-xs uppercase tracking-widest text-black hover:text-primary transition-colors"
          >
            Build a Quote
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="flex items-center gap-2 rounded-full pl-1 pr-1 sm:pr-3 py-1 hover:bg-zinc-100 transition-colors outline-none ring-primary/40 focus-visible:ring-2"
                >
                  <UserAvatar name={user.name} email={user.email} src={user.avatar_url} testid="nav-avatar" />
                  <span className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="font-mono text-xs font-medium text-zinc-900 max-w-[140px] truncate">{user.name || user.email}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{user.role}</span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-none border-zinc-900">
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                  Signed in as
                  <p className="mt-0.5 text-zinc-900 normal-case tracking-normal text-xs truncate">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate(dashboardPath(user.role))} className="rounded-none font-mono text-xs uppercase tracking-widest cursor-pointer">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-users" onClick={() => navigate("/admin/users")} className="rounded-none font-mono text-xs uppercase tracking-widest cursor-pointer">
                    <Users className="h-4 w-4 mr-2" /> Users
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")} className="rounded-none font-mono text-xs uppercase tracking-widest cursor-pointer">
                  <Settings className="h-4 w-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-logout-btn" onClick={handleLogout} className="rounded-none font-mono text-xs uppercase tracking-widest cursor-pointer text-red-600 focus:text-red-700">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              {location.pathname !== "/login" && (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="nav-login-btn"
                  onClick={() => navigate("/login")}
                  className="rounded-none font-mono text-xs uppercase tracking-widest text-black"
                >
                  Sign In
                </Button>
              )}
              <Button
                size="sm"
                data-testid="nav-cta-btn"
                onClick={() => navigate("/build")}
                className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono text-xs uppercase tracking-widest"
              >
                Get a Quote
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
